/**
 * Empty state shown when no API sequence exists for the current workflow.
 *
 * @module FlowDesigner/api/ApiSequenceEmptyState
 */

import React from 'react';
import { Lightning, Warning, CheckCircle, XCircle } from '@phosphor-icons/react';
import { Button, Text, Flex, Spinner, Switch } from '@chatsdet/ui';
import { ApiSequenceLogEntry } from '../../../components/node-config/ApiSequenceLogEntry';
import { buildLogEntries } from '../../../components/node-config/buildLogEntries';
import type { AgentEvent } from '../../../../hooks/useApiSequenceGenerationStream';

type GenerateStatus = 'idle' | 'running' | 'complete' | 'error';

type ApiSequenceEmptyStateProps = {
  workflowId: string;
  nodeCount: number;
  hasSuccessfulRun: boolean;
  isGenerating: boolean;
  generateStatus: GenerateStatus;
  generateEvents: AgentEvent[];
  onGenerate: (guidancePrompt?: string) => void;
  onGenerateFromPrompt?: (prompt: string) => void;
  onCancel: () => void;
  generateError: string | null;
  apiOnly?: boolean;
  onApiOnlyToggle?: (apiOnly: boolean) => void;
  onEditDirectly?: () => void;
};

export const ApiSequenceEmptyState: React.FC<ApiSequenceEmptyStateProps> = ({
  nodeCount,
  hasSuccessfulRun,
  isGenerating,
  generateStatus,
  generateEvents,
  onGenerate,
  onGenerateFromPrompt,
  onCancel,
  generateError,
  apiOnly = false,
  onApiOnlyToggle,
  onEditDirectly,
}) => {
  const [guidancePrompt, setGuidancePrompt] = React.useState('');
  const isComplex = nodeCount >= 10;
  const logEntries = buildLogEntries(generateEvents, generateStatus);
  const isSaving = generateStatus === 'complete' && logEntries.length > 0;

  // In API-only mode with no successful run, we use prompt-based generation
  const usePromptMode = apiOnly && !hasSuccessfulRun;
  const canGenerate = usePromptMode
    ? guidancePrompt.trim().length > 0
    : hasSuccessfulRun;

  if (isGenerating || isSaving) {
    return (
      <div className="flex-1 flex flex-col items-center justify-start p-8 overflow-y-auto">
        <div className="w-full max-w-xl space-y-3">
          <Flex align="center" gap="sm" className="mb-4">
            <Spinner size="md" className="text-primary" />
            <Text weight="semibold">
              {isSaving ? 'Saving API sequence...' : 'Generating API sequence...'}
            </Text>
          </Flex>
          <div className="space-y-1">
            {logEntries.map((entry) => (
              <ApiSequenceLogEntry
                key={entry.key}
                icon={entry.icon}
                text={entry.text}
                detail={entry.detail}
                badge={entry.badge}
              />
            ))}
          </div>
          {isGenerating && (
            <div className="pt-4">
              <Button variant="secondary" size="sm" onPress={onCancel}>
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="text-center max-w-md space-y-4">
        <div className="flex justify-center">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Lightning size={24} className="text-primary" weight="duotone" />
          </div>
        </div>

        <div className="space-y-2">
          <Text size="lg" weight="semibold">
            Generate API Sequence
          </Text>
          <Text className="text-muted-foreground">
            {apiOnly
              ? 'Create an API-only sequence from a prompt or workflow run.'
              : 'Convert this workflow\'s browser actions into fast API calls for data generation.'}
          </Text>
        </div>

        {onApiOnlyToggle && (
          <Switch
            isSelected={apiOnly}
            onChange={(val) => onApiOnlyToggle(val)}
            size="sm"
            label="API-only execution"
          />
        )}

        {!apiOnly && !hasSuccessfulRun && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-warning/10 border border-warning/20 text-left">
            <XCircle size={18} className="text-warning flex-shrink-0 mt-0.5" weight="fill" />
            <Text size="sm" className="text-warning">
              Run this workflow successfully first. A successful run with diagnostics is required to generate an API sequence.
            </Text>
          </div>
        )}

        {apiOnly && !hasSuccessfulRun && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-primary/10 border border-primary/20 text-left">
            <Lightning size={18} className="text-primary flex-shrink-0 mt-0.5" weight="fill" />
            <Text size="sm" className="text-primary">
              Describe what the API sequence should do. It will be generated from your prompt using the API catalog.
            </Text>
          </div>
        )}

        {hasSuccessfulRun && isComplex && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-warning/10 border border-warning/20 text-left">
            <Warning size={18} className="text-warning flex-shrink-0 mt-0.5" weight="fill" />
            <Text size="sm" className="text-warning">
              This workflow has {nodeCount} steps. Complex workflows may not convert fully to API calls
              — some browser-only interactions may be skipped.
            </Text>
          </div>
        )}

        {hasSuccessfulRun && !isComplex && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-success/10 border border-success/20 text-left">
            <CheckCircle size={18} className="text-success flex-shrink-0 mt-0.5" weight="fill" />
            <Text size="sm" className="text-success">
              Ready to generate. This workflow has {nodeCount} step{nodeCount !== 1 ? 's' : ''}.
            </Text>
          </div>
        )}

        {generateError && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-left">
            <XCircle size={18} className="text-destructive flex-shrink-0 mt-0.5" weight="fill" />
            <Text size="sm" className="text-destructive">
              {generateError}
            </Text>
          </div>
        )}

        <div className="w-full text-left space-y-1">
          <Text size="sm" weight="medium" className="text-muted-foreground">
            {usePromptMode ? 'Prompt (required)' : 'Guidance (optional)'}
          </Text>
          <textarea
            value={guidancePrompt}
            onChange={(e) => setGuidancePrompt(e.target.value)}
            placeholder={
              usePromptMode
                ? 'Describe what the API sequence should do...'
                : 'Describe any specific requirements for the API sequence...'
            }
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
            rows={3}
          />
        </div>

        <div className="flex flex-col items-stretch gap-3 pt-2">
          <Button
            variant="primary"
            onPress={() => {
              if (usePromptMode && onGenerateFromPrompt) {
                onGenerateFromPrompt(guidancePrompt);
              } else {
                onGenerate(guidancePrompt || undefined);
              }
            }}
            isDisabled={!canGenerate}
          >
            <Lightning size={16} weight="fill" />
            Generate API Sequence
          </Button>
          {apiOnly && onEditDirectly && (
            <>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-px bg-border" />
                <Text size="xs" className="text-muted-foreground uppercase tracking-wide">
                  or
                </Text>
                <div className="flex-1 h-px bg-border" />
              </div>
              <Button variant="secondary" onPress={onEditDirectly}>
                Edit directly
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
